<template>
  <div>
    <Loader bgColor="#fff" v-if="isLoading"></Loader>
    <template v-else>
      <div class="do-table__filters">
        <div class="do-table__filters__left">
          <Tabs
            style="height: 26px"
            :tabs="templateTabs"
            :selected="selectedTemplateTab"
            :isLarge="false"
            @select="selectedTemplateTab = $event">
          </Tabs>
          <DoFiltersFilter
            title="Personer"
            :tags="personNames"
            :selectedTags="selectedNames"
            :numberOfSelectedTagsToShow="1"
            @select="toggleName"
            @clear="selectedNames = []">
          </DoFiltersFilter>
          <DoFiltersFilter
            title="Grupper"
            :tags="tags"
            :selectedTags="selectedTags"
            :numberOfSelectedTagsToShow="1"
            @select="toggleTag"
            @clear="selectedTags = []">
          </DoFiltersFilter>
          <DoFiltersFilter
            v-if="employmentTitles.length > 1"
            title="Ansættelser"
            :tags="employmentTitles"
            :selectedTags="selectedEmploymentTitles"
            :numberOfSelectedTagsToShow="1"
            @select="toggleEmploymentTitle"
            @clear="selectedEmploymentTitles = []">
          </DoFiltersFilter>
          <DoFiltersFilter
            v-if="organisations.length > 1"
            title="Organisationer"
            :tags="organisations"
            :selectedTags="selectedOrganisations"
            :numberOfSelectedTagsToShow="1"
            @select="selectedOrganisation"
            @clear="selectedOrganisations = []">
          </DoFiltersFilter>
          <span class="do-table__filters__add__explainer-text">Viser {{ filteredUsers.length }} af {{ users.length }} brugere</span>
        </div>
        <div class="do-table__filters__add">
          <DoEmploymentDropdown
            :employmentTypes="employmentTemplates"
            :selectedEmploymentIds="selectedEmploymentIds"
            @onTemplateCreated="onTemplateCreated"
            @onTemplateUpdated="onTemplateUpdated"
            @onTemplateDeleted="onTemplateDeleted"
            @select="onTemplateSelected"
          />
          <div class="do-spinner" v-if="isSubmitting"></div>
          <span v-else>Valgt {{
              selectedEmploymentIds.length
            }} {{ selectedEmploymentIds.length === 1 ? 'ansættelse' : 'ansættelser' }}</span>
        </div>
      </div>

      <SalarySettingsUsersTable
        :isSalaryIntegrationActive="isSalaryIntegrationActive"
        :filteredUsers="filteredUsers"
        :isImportOfEmployments="true"
        :employmentTemplates="employmentTemplates"
        :selectedEmploymentIds="selectedEmploymentIds"
        :isShowEmploymentNumber="isShowEmploymentNumber"
        :rowActionLabel="rowActionLabel"
        @toggleEmployment="toggleEmployment"
        @toggleAllEmployments="toggleAllEmployments"
        @editEmployment="onEditEmployment"
        @setTheEditUser="$emit('setTheEditUser', $event)"
        @editSsn="onEditSsn"
        @removeEmployment="employmentRemovalTarget = $event"
      />

      <SalarySsnModal
        v-if="theSsnUser"
        :user="theSsnUser"
        @close="theSsnUser = null"
      ></SalarySsnModal>

      <DModal
        v-if="templateRemovalTarget"
        :show-close="false"
        :isBgOverlay="true"
        :appendToBody="true"
        style="z-index: 12312312312312"
        @close="cancelRemoveTemplate"
      >
        <div class="remove-template-modal text-dark" slot="insertComponent" @click.stop>
          <div class="remove-template-modal__header">
            <h2>Fjern stillingskategori</h2>
            <div class="font-large icon__bg-circle remove-template-modal__close" @click="cancelRemoveTemplate">
              <span class="icon-cross2"></span>
            </div>
          </div>

          <div class="remove-template-modal__content">
            <p>
              Ønsker du at fjerne stillingskategorien
              <strong v-if="templateRemovalTarget.employment.title">{{ templateRemovalTarget.employment.title }}</strong>
              fra ansættelsen på <strong>{{ templateRemovalTarget.user.name }}</strong>?
            </p>
          </div>

          <div class="remove-template-modal__buttons">
            <div></div>
            <div>
              <button class="do-bttn" @click="cancelRemoveTemplate">
                {{ $t('vue2.common.cancel') }}
              </button>
              <button
                class="do-bttn do-bttn--submit"
                :class="{
                  'do-bttn--submit--disabled': isRemovingTemplate,
                  'do-bttn--submit--loading': isRemovingTemplate
                }"
                @click="confirmRemoveTemplate"
              >
                Fjern
              </button>
            </div>
          </div>
        </div>
      </DModal>

      <DModal
        v-if="employmentRemovalTarget"
        :show-close="false"
        :isBgOverlay="true"
        :appendToBody="true"
        style="z-index: 12312312312312"
        @close="cancelRemoveEmployment"
      >
        <div class="remove-template-modal text-dark" slot="insertComponent" @click.stop>
          <div class="remove-template-modal__header">
            <h2>Fjern ansættelse</h2>
            <div class="font-large icon__bg-circle remove-template-modal__close" @click="cancelRemoveEmployment">
              <span class="icon-cross2"></span>
            </div>
          </div>

          <div class="remove-template-modal__content">
            <p>
              Vil du fjerne ansættelsen
              <strong>
                <template v-if="employmentRemovalTarget.employment.employment_number">{{ employmentRemovalTarget.employment.employment_number }} -</template>
                {{ employmentRemovalTarget.employment.source_title || employmentRemovalTarget.employment.title }}
              </strong>
              for <strong>{{ employmentRemovalTarget.user.name }}</strong> i Docendo?
            </p>
            <p>Den vil stadig være i lønsystemet.</p>
          </div>

          <div class="remove-template-modal__buttons">
            <div></div>
            <div>
              <button class="do-bttn" @click="cancelRemoveEmployment">
                {{ $t('vue2.common.cancel') }}
              </button>
              <button
                class="do-bttn do-bttn--submit"
                :class="{
                  'do-bttn--submit--disabled': isRemovingEmployment,
                  'do-bttn--submit--loading': isRemovingEmployment
                }"
                @click="confirmRemoveEmployment"
              >
                Fjern ansættelse
              </button>
            </div>
          </div>
        </div>
      </DModal>
    </template>
  </div>
</template>

<script>
import DModal from '../../common/DModal.vue'
import Loader from '../../common/Loader.vue'
import SalarySettingsUsersTable from './SalarySettingsUsersOpusTable.vue'
import SalarySsnModal from './SalarySsnModal.vue'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
import TaggingFilter from '../../Tagging/TaggingFilter.vue'
import DoEmploymentDropdown from './DoEmploymentDropdown.vue'
import docendo from '../../../docendo'
import eventbus from '../../../eventbus'
import Tabs from '../../common/Tabs.vue'

export default {
  components: {Tabs, DoEmploymentDropdown, TaggingFilter, DoFiltersFilter, SalarySettingsUsersTable, SalarySsnModal, Loader, DModal},
  props: {
    users: {
      default: () => []
    },
    employments: {
      default: () => []
    },
    tags: {
      default: () => []
    },
    isSalaryIntegrationActive: {
      type: Boolean,
      default: false
    },
    isShowEmploymentNumber: {
      type: Boolean,
      default: false
    },
    filtersStorageKey: {
      type: String,
      default: 'salaryUsersOpusFilters'
    },
    rowActionLabel: {
      type: String,
      default: ''
    },
    // Når true åbnes en bekræftelses-modal for at fjerne stillingskategorien,
    // i stedet for at videresende editEmployment-eventet til parent.
    confirmRemoveTemplateOnTagClick: {
      type: Boolean,
      default: false
    },
    // Når true videresendes "Indtast CPR"-klik til parent (emit 'editSsn')
    // i stedet for at åbne den interne CPR-modal. Bruges hvor CPR og
    // ansættelsesnummer indtastes i samme modal.
    emitEditSsn: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      selectedTags: [],
      selectedEmploymentTitles: [],
      selectedOrganisations: [],
      selectedEmploymentIds: [],
      isSubmitting: false,
      selectedNames: [],
      selectedTemplateTab: 'all', // 'all' | 'with_template'
      theSsnUser: null,
      templateRemovalTarget: null,
      isRemovingTemplate: false,
      employmentRemovalTarget: null,
      isRemovingEmployment: false
    }
  },
  computed: {
    employmentTemplates () {
      return this.$store.getters.employmentTemplatesSorted
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    storageKey () {
      return this.filtersStorageKey + ':' + (this.schoolId || 'default')
    },
    filtersSnapshot () {
      return JSON.stringify({
        selectedTags: this.selectedTags,
        selectedEmploymentTitles: this.selectedEmploymentTitles,
        selectedOrganisations: this.selectedOrganisations,
        selectedNames: this.selectedNames,
        selectedTemplateTab: this.selectedTemplateTab
      })
    },
    personNames () {
      const names = new Set()
      this.users.forEach(u => {
        if (u && u.name) names.add(u.name)
      })
      return Array.from(names).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    organisations () {
      const organisations = new Set()
      this.employments.forEach(employment => {
        if (employment.organisation) {
          organisations.add(employment.organisation)
        }
      })
      return Array.from(organisations).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },

    // NYT: unikke ansættelsestitler
    employmentTitles () {
      const titles = new Set()
      this.employments.forEach(e => {
        if (e.title) titles.add(e.title)
      })
      return Array.from(titles).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    hasEmploymentFilter () {
      return this.selectedOrganisations.length > 0 || this.selectedEmploymentTitles.length > 0
    },

    // Udvidet: filtrer nu både på organisationer og ansættelsestitler
    filteredEmployments () {
      const hasOrgFilter = this.selectedOrganisations.length > 0
      const hasTitleFilter = this.selectedEmploymentTitles.length > 0

      return this.employments.filter(e => {
        const orgOk = hasOrgFilter ? this.selectedOrganisations.indexOf(e.organisation) > -1 : true
        const titleOk = hasTitleFilter ? this.selectedEmploymentTitles.indexOf(e.title) > -1 : true
        return orgOk && titleOk
      })
    },

    filteredUsers () {
      const allEmploymentsForUser = userId => {
        return this.employments.filter(e => e.user_id === userId)
      }

      const matchedEmploymentsForUser = userId => {
        return this.filteredEmployments.filter(e => e.user_id === userId)
      }

      return this.users
        // 0) filter på navne
        .filter(user => {
          if (this.selectedNames.length > 0) {
            return this.selectedNames.indexOf(user.name) > -1
          } else {
            return true
          }
        })
        // 1) filter på tags
        .filter(user => {
          if (this.selectedTags.length > 0) {
            return user.user_tags && user.user_tags.some(tag => this.selectedTags.indexOf(tag) > -1)
          } else {
            return true
          }
        })
        // 2) filter personer på ansættelses-match
        .filter(user => {
          if (!this.hasEmploymentFilter) return true
          // Brugere uden ansættelser vises stadig ved organisationsfilter
          if (this.selectedOrganisations.length > 0 &&
            this.selectedEmploymentTitles.length === 0 &&
            allEmploymentsForUser(user.id).length === 0) return true
          const emps = matchedEmploymentsForUser(user.id)
          return emps.length > 0
        })
        // 2b) NYT: tab-filter "Alle" vs "Med skabelon"
        .filter(user => {
          if (this.selectedTemplateTab === 'all') return true
          const emps = this.hasEmploymentFilter
            ? matchedEmploymentsForUser(user.id)
            : allEmploymentsForUser(user.id)
          return emps.some(e => this.hasStillingskategori(e))
        })
        // 3) byg v_employments til visning
        .map(user => {
          const emps = this.hasEmploymentFilter
            ? matchedEmploymentsForUser(user.id)
            : allEmploymentsForUser(user.id)

          return {
            ...user,
            v_employments: emps.sort((a, b) => {
              const aTitle = a && (a.source_title || a.title) ? (a.source_title || a.title) : ''
              const bTitle = b && (b.source_title || b.title) ? (b.source_title || b.title) : ''
              return aTitle.localeCompare(bTitle, 'da-DK')
            })
          }
        })
    },
    usersWithTemplateCount () {
      const allEmploymentsForUser = userId => this.employments.filter(e => e.user_id === userId)
      let count = 0
      this.users.forEach(u => {
        const emps = allEmploymentsForUser(u.id)
        const hasTemplate = emps.some(e => this.hasStillingskategori(e))
        if (hasTemplate) count = count + 1
      })
      return count
    },

    templateTabs () {
      const allCount = this.users.length
      const withTpl = this.usersWithTemplateCount
      return [
        { id: 'all', title: 'Alle (' + allCount + ')' },
        { id: 'with_template', title: 'Med stillingskategori (' + withTpl + ')' }
      ]
    }
  },
  watch: {
    filtersSnapshot (val) {
      this.persistFilters(val)
    },
    selectedNames () {
      this.selectedEmploymentIds = []
    },
    selectedTags () {
      this.selectedEmploymentIds = []
    },
    selectedEmploymentTitles () {
      this.selectedEmploymentIds = []
    },
    selectedOrganisations () {
      this.selectedEmploymentIds = []
    }
  },
  methods: {
    hasStillingskategori (employment) {
      return !!employment && employment.employment_template_id != null && employment.employment_template_id !== undefined
    },
    onEditSsn (user) {
      if (this.emitEditSsn) {
        this.$emit('editSsn', user)
      } else {
        this.theSsnUser = user
      }
    },
    onEditEmployment (payload) {
      if (this.confirmRemoveTemplateOnTagClick) {
        this.templateRemovalTarget = payload
      } else {
        this.$emit('editEmployment', payload)
      }
    },
    cancelRemoveTemplate () {
      if (this.isRemovingTemplate) return
      this.templateRemovalTarget = null
    },
    confirmRemoveTemplate () {
      if (this.isRemovingTemplate || !this.templateRemovalTarget) return
      const employment = this.templateRemovalTarget.employment
      this.isRemovingTemplate = true
      // Stillingskategorien kopierer vikarløn/lønkode over på ansættelsen – ryd dem når den fjernes.
      docendo.updateEmployment(
        this.schoolId,
        employment.user_id,
        employment.id,
        false,
        null,
        null,
        null,
        null,
        null,
        success => {
          this.isRemovingTemplate = false
          this.templateRemovalTarget = null
          this.$store.dispatch('readEmployments')
          eventbus.toast = {text: 'Stillingskategori er fjernet', success: true, position: 'bottomLeft'}
        },
        failure => {
          console.log(failure)
          this.isRemovingTemplate = false
          eventbus.toast = {text: 'Kunne ikke fjerne stillingskategori', success: false, position: 'bottomLeft'}
        }
      )
    },
    cancelRemoveEmployment () {
      if (this.isRemovingEmployment) return
      this.employmentRemovalTarget = null
    },
    confirmRemoveEmployment () {
      if (this.isRemovingEmployment || !this.employmentRemovalTarget) return
      const employment = this.employmentRemovalTarget.employment
      this.isRemovingEmployment = true
      // Ansættelser med registrerede timer bliver arkiveret (markeret som slettet) i stedet for at blive slettet.
      docendo.deleteEmployment(this.schoolId, employment.user_id, employment.id, data => {
        this.isRemovingEmployment = false
        if (data && data.status === 'error') {
          eventbus.toast = {text: data.message || 'Kunne ikke fjerne ansættelsen', success: false, position: 'bottomLeft'}
          return
        }
        this.employmentRemovalTarget = null
        this.selectedEmploymentIds = this.selectedEmploymentIds.filter(id => id !== employment.id)
        this.$store.dispatch('readEmployments')
        eventbus.toast = {text: 'Ansættelsen er fjernet', success: true, position: 'bottomLeft'}
      }, failure => {
        console.log(failure)
        this.isRemovingEmployment = false
        eventbus.toast = {text: 'Kunne ikke fjerne ansættelsen', success: false, position: 'bottomLeft'}
      })
    },
    toggleName (name) {
      const idx = this.selectedNames.indexOf(name)
      if (idx > -1) {
        this.selectedNames.splice(idx, 1)
      } else {
        this.selectedNames.push(name)
      }
    },
    onTemplateSelected (employmentType) {
      this.isSubmitting = true
      const employmentTemplateId = employmentType.id
      const employmentIds = this.selectedEmploymentIds
      this.selectedEmploymentIds = []
      docendo.assignEmploymentTemplateToEmployment(this.schoolId, employmentTemplateId, employmentIds, success => {
        this.isSubmitting = false
        this.$store.dispatch('readEmployments')
      }, failure => {
        this.isSubmitting = false
      })
    },
    toggleEmployment (employment) {
      const id = employment.id
      const idx = this.selectedEmploymentIds.indexOf(id)
      if (idx > -1) {
        this.selectedEmploymentIds.splice(idx, 1)
      } else {
        this.selectedEmploymentIds.push(id)
      }
    },

    toggleAllEmployments () {
      // Saml alle synlige ansættelser (efter filtre)
      const visibleEmploymentIds = []
      this.filteredUsers.forEach(user => {
        user.v_employments.forEach(e => visibleEmploymentIds.push(e.id))
      })

      if (this.selectedEmploymentIds.length > 0) {
        // Fravælg alle synlige (kun dem der vises lige nu)
        this.selectedEmploymentIds = this.selectedEmploymentIds.filter(id => visibleEmploymentIds.indexOf(id) === -1)
      } else {
        // Vælg alle synlige
        this.selectedEmploymentIds = visibleEmploymentIds.slice()
      }
    },
    onTemplateCreated () {
      this.readEmploymentTemplates()
    },
    onTemplateUpdated () {
      this.readEmploymentTemplates()
    },
    onTemplateDeleted () {
      this.readEmploymentTemplates()
    },
    toggleTag (tag) {
      const idx = this.selectedTags.indexOf(tag)
      if (idx > -1) {
        this.selectedTags.splice(idx, 1)
      } else {
        this.selectedTags.push(tag)
      }
    },
    selectedOrganisation (organisation) {
      const idx = this.selectedOrganisations.indexOf(organisation)
      if (idx > -1) {
        this.selectedOrganisations.splice(idx, 1)
      } else {
        this.selectedOrganisations.push(organisation)
      }
    },

    // NYT: toggle for ansættelsestitel
    toggleEmploymentTitle (title) {
      const idx = this.selectedEmploymentTitles.indexOf(title)
      if (idx > -1) {
        this.selectedEmploymentTitles.splice(idx, 1)
      } else {
        this.selectedEmploymentTitles.push(title)
      }
    },

    onClose () {
      this.$emit('close')
    },
    readEmploymentTemplates () {
      this.$store.dispatch('readEmploymentTemplates')
    },
    persistFilters (val) {
      try {
        window.localStorage.setItem(this.storageKey, val)
      } catch (e) {
        // ignore storage write errors (e.g. private mode / quota)
      }
    },
    restoreFilters () {
      try {
        const raw = window.localStorage.getItem(this.storageKey)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (!saved) return
        if (Array.isArray(saved.selectedTags)) this.selectedTags = saved.selectedTags
        if (Array.isArray(saved.selectedEmploymentTitles)) this.selectedEmploymentTitles = saved.selectedEmploymentTitles
        if (Array.isArray(saved.selectedOrganisations)) this.selectedOrganisations = saved.selectedOrganisations
        if (Array.isArray(saved.selectedNames)) this.selectedNames = saved.selectedNames
        if (saved.selectedTemplateTab) this.selectedTemplateTab = saved.selectedTemplateTab
      } catch (e) {
        // ignore malformed storage
      }
    }
  },
  created () {
    this.restoreFilters()
  },
  mounted () {
    this.readEmploymentTemplates()
  }
}
</script>

<style scoped>
.d-modal {
  width: calc(100vw - 3rem);
  height: calc(100vw - 3rem);
  border-radius: 16px;
  position: relative;
}

.d-modal__white {
  background: #fff;
}

.d-modal__header__custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem 0 1rem;
}

.do-table__filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  border-bottom: 0;
  padding: 8px;
  border-radius: 4px 4px 0 0;
}

.do-table__filters__left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.do-table__filters__add {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.do-table__filters__add span,
.do-table__filters__add__explainer-text {
  font-style: italic;
  font-size: 11px;
}

.salary-rules__rule-container {
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  font-style: italic;
}

.remove-template-modal {
  width: 460px;
  max-width: calc(100vw - 3rem);
  border-radius: 16px;
  position: relative;
}

.remove-template-modal__header {
  background: #fff;
  padding: 1rem;
  border-radius: 16px 16px 0 0;
  border-bottom: 1px solid #d8d8d8;
  position: relative;
}

.remove-template-modal__header h2 {
  margin: 0;
}

.remove-template-modal__close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 1.2rem;
  opacity: 0.85;
  cursor: pointer;
}

.remove-template-modal__content {
  padding: 1.5rem 1rem;
  line-height: 1.4;
}

.remove-template-modal__content p {
  margin: 0;
}

.remove-template-modal__content p + p {
  margin-top: 0.75rem;
}

.remove-template-modal__buttons {
  display: flex;
  justify-content: space-between;
  padding: 1rem;
}

.remove-template-modal__buttons > div {
  display: flex;
  align-items: center;
  gap: 1rem;
}
</style>



// WEBPACK FOOTER //
// SalarySettingsUsersOpus.vue?f656b306